import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { ErrorBanner } from '@/components/app/error-banner'
import { LogoMark } from '@/components/app/logo-mark'
import { SubmitButton } from '@/components/app/submit-button'
import { PhoneInput } from '@/components/ui/phone-input'
import { apiPost } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { setAuthFlow } from '@/lib/auth-flow'

export const Route = createFileRoute('/connexion/')({
  component: PhoneScreen,
})

/** Pays d'usage courant pour la diaspora ivoirienne — repris de l'app native. */
const COUNTRIES = ['CI', 'SN', 'CM', 'BJ', 'TG', 'ML', 'BF', 'GN', 'FR'] as const

function PhoneScreen() {
  const navigate = useNavigate()
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const isValid = phone.length > 0 && isValidPhoneNumber(phone)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!isValid || loading) return

    setError(null)
    setLoading(true)

    try {
      await apiPost('auth/mobile/request-otp', { phone })
      setAuthFlow({ phone })
      await navigate({ to: '/connexion/code' })
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible d'envoyer le code."))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-navy-950 px-6 pt-safe pb-safe">
      <div className="mx-auto w-full max-w-sm">
        <LogoMark caption="CONNEXION" />

        <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-5">
          <div>
            <h2 className="text-[20px] font-semibold text-chalk">Votre numéro de téléphone</h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-steel">
              Nous vous enverrons un code de vérification par SMS.
            </p>
          </div>

          <PhoneInput
            value={phone}
            onChange={(value) => setPhone(value ?? '')}
            defaultCountry="CI"
            countries={[...COUNTRIES]}
            international
            placeholder="07 00 00 00 00"
            autoComplete="tel"
            aria-label="Numéro de téléphone"
          />

          <ErrorBanner message={error} />

          <SubmitButton disabled={!isValid} loading={loading}>
            Recevoir le code
          </SubmitButton>
        </form>
      </div>
    </div>
  )
}
